import { createDefaultInstrumentPatch, normalizeInstrumentPatch } from './patchSchema';
import { defaultCoreFx, defaultCorePatch, defaultLfo, defaultSlot } from './synthCoreSchema';
import { normalizeArpPattern } from './arpeggiator';

/**
 * Factory patches for the CookUp Core engine. Each is one Core layer with its
 * own effects rack; the instrument's shared Web Audio effects stay off.
 */
const osc = (overrides) => ({ ...defaultCorePatch().oscA, ...overrides });
const env = (overrides) => ({ ...defaultCorePatch().ampEnv, ...overrides });
const voice = (overrides) => ({ ...defaultCorePatch().voice, ...overrides });
/** LFOs by index: { 0: { shape, rate, … } }. */
const lfos = (settings) => Array.from({ length: 4 }, (_, index) => ({ ...defaultLfo(), ...(settings[index] || {}) }));
/** Mod-matrix routes: [source, destination, amount, via?]. */
const routes = (...list) => Array.from({ length: 16 }, (_, index) => (list[index]
  ? { source: list[index][0], destination: list[index][1], amount: list[index][2], via: list[index][3] || 'none' }
  : defaultSlot()));
/** An arp step pattern from a compact spec: '.' rest, 'x' play, '^' octave up, '_' octave down, '-' tie. */
const steps = (spec, velocities = []) => {
  const pattern = normalizeArpPattern({ steps: spec.length }).pattern;
  [...spec].forEach((symbol, index) => {
    pattern[index] = {
      on: symbol !== '.',
      velocity: velocities[index] ?? 1,
      octave: symbol === '^' ? 1 : symbol === '_' ? -1 : 0,
      tie: symbol === '-'
    };
  });
  return { steps: spec.length, pattern };
};
/** A rack with the named effects switched on, in the default order. */
const rack = (effects) => {
  const fx = defaultCoreFx();
  Object.entries(effects).forEach(([key, settings]) => { fx[key] = { ...fx[key], ...settings, enabled: true }; });
  return fx;
};

const CORE_PATCHES = [
  {
    name: 'Core Supersaw Lead', category: 'Lead', tags: ['Core', 'Supersaw', 'Wide'],
    core: {
      voice: voice({ level: 1.5 }),
      oscA: osc({ position: 2 / 3, unison: 9, detune: 38, blend: 0.8, width: 1, level: 0.8 }),
      oscB: osc({ enabled: true, position: 2 / 3, semi: 12, unison: 5, detune: 22, blend: 0.6, width: 0.9, level: 0.35 }),
      filter: { enabled: true, mode: 'lp24', cutoff: 5200, resonance: 0.18, drive: 0.12, keytrack: 0.3, envAmount: 1.6, velocityAmount: 0.4 },
      ampEnv: env({ attack: 0.004, decay: 0.3, sustain: 0.85, release: 0.35 }),
      modEnv: env({ attack: 0.001, decay: 0.45, sustain: 0.2, release: 0.3 }),
      fx: rack({
        hyper: { mix: 0.3, detune: 0.3, dimension: 0.25 },
        delay: { sync: true, division: 0.75, mode: 'ping-pong', feedback: 0.35, mix: 0.16, highCut: 7000 },
        multiband: { depth: 0.25 },
        reverb: { size: 1.2, decay: 2.2, mix: 0.16 }
      })
    }
  },
  {
    name: 'Core 808 Glide', category: 'Bass', tags: ['Core', '808', 'Legato', 'Glide'],
    core: {
      voice: { mode: 'legato', voices: 1, glide: 0.09, glideMode: 'legato', velocityToAmp: 0.35, modWheelToCutoff: 1.5, level: 0.2 },
      oscA: osc({ position: 0, unison: 1, randomPhase: 0, level: 0.95 }),
      oscB: osc({ enabled: true, position: 1 / 3, unison: 1, randomPhase: 0, level: 0.18 }),
      filter: { enabled: true, mode: 'lp12', cutoff: 900, resonance: 0.05, drive: 0.45, keytrack: 0.5, envAmount: 1.2, velocityAmount: 0.6 },
      ampEnv: env({ attack: 0.001, hold: 0.02, decay: 2.2, sustain: 0.0, release: 0.25, decayCurve: 0.35 }),
      modEnv: env({ attack: 0.001, decay: 0.12, sustain: 0, release: 0.1 }),
      fx: rack({ distortion: { mode: 'tube', drive: 0.3, mix: 0.6, tone: 9000 } })
    }
  },
  {
    name: 'Core PWM Pad', category: 'Pad', tags: ['Core', 'Pad', 'Evolving'],
    core: {
      voice: voice({ level: 0.93 }),
      oscA: osc({ table: 1, position: 0.25, envToPosition: 0.45, unison: 5, detune: 18, blend: 0.7, width: 0.9, level: 0.7 }),
      oscB: osc({ enabled: true, table: 2, position: 0.35, octave: -1, unison: 3, detune: 10, level: 0.4 }),
      filter: { enabled: true, mode: 'lp24', cutoff: 2600, resonance: 0.22, drive: 0, keytrack: 0.5, envAmount: 1.2, velocityAmount: 0 },
      ampEnv: env({ attack: 0.7, decay: 1.5, sustain: 0.85, release: 2.2, attackCurve: -0.2 }),
      modEnv: env({ attack: 2.6, decay: 3, sustain: 0.6, release: 2, attackCurve: -0.3 }),
      fx: rack({
        chorus: { mix: 0.45, depth: 0.5 },
        reverb: { size: 1.8, decay: 5, preDelay: 30, mix: 0.35 }
      })
    }
  },
  {
    name: 'Core Vowel Pluck', category: 'Pluck', tags: ['Core', 'Formant', 'Pluck'],
    core: {
      voice: voice({ level: 1.5 }),
      oscA: osc({ table: 4, position: 0.1, envToPosition: 0.75, unison: 3, detune: 12, width: 0.7, level: 0.85 }),
      filter: { enabled: true, mode: 'lp12', cutoff: 1400, resonance: 0.3, drive: 0.1, keytrack: 0.6, envAmount: 3.8, velocityAmount: 1 },
      ampEnv: env({ attack: 0.001, decay: 0.55, sustain: 0, release: 0.35 }),
      modEnv: env({ attack: 0.001, decay: 0.32, sustain: 0, release: 0.2 }),
      fx: rack({
        delay: { sync: true, division: 0.75, mode: 'ping-pong', style: 'tape', feedback: 0.35, mix: 0.2 },
        reverb: { decay: 1.8, mix: 0.2 }
      })
    }
  },
  {
    name: 'Core Sync Lead', category: 'Lead', tags: ['Core', 'Sync', 'Mono'],
    core: {
      voice: { mode: 'mono', voices: 1, glide: 0.05, glideMode: 'legato', velocityToAmp: 0.5, modWheelToCutoff: 2, level: 0.47 },
      oscA: osc({ table: 3, position: 0.15, envToPosition: 0.6, unison: 3, detune: 10, width: 0.5, level: 0.8 }),
      sub: { level: 0.35, octave: -1, shape: 'square', pan: 0 },
      filter: { enabled: true, mode: 'lp24', cutoff: 4200, resonance: 0.25, drive: 0.35, keytrack: 0.4, envAmount: 1.5, velocityAmount: 0.5 },
      ampEnv: env({ attack: 0.003, decay: 0.4, sustain: 0.8, release: 0.2 }),
      modEnv: env({ attack: 0.001, decay: 0.6, sustain: 0.1, release: 0.2 }),
      fx: rack({
        distortion: { mode: 'soft', drive: 0.25, mix: 0.5 },
        delay: { sync: true, division: 0.5, feedback: 0.3, mix: 0.12 },
        reverb: { mix: 0.12 }
      })
    }
  },
  {
    name: 'Core Resonant Bass', category: 'Bass', tags: ['Core', 'Acid', 'Resonant'],
    core: {
      voice: { mode: 'mono', voices: 1, glide: 0.06, glideMode: 'legato', velocityToAmp: 0.4, modWheelToCutoff: 2.5, level: 0.4 },
      oscA: osc({ position: 2 / 3, unison: 1, randomPhase: 0, level: 0.85 }),
      sub: { level: 0.45, octave: -1, shape: 'sine', pan: 0 },
      filter: { enabled: true, mode: 'ladder24', cutoff: 260, resonance: 0.72, drive: 0.4, keytrack: 0.3, envAmount: 4.2, velocityAmount: 1.2 },
      ampEnv: env({ attack: 0.002, decay: 0.3, sustain: 0.7, release: 0.12 }),
      modEnv: env({ attack: 0.001, decay: 0.22, sustain: 0.05, release: 0.12 }),
      fx: rack({
        distortion: { mode: 'tube', drive: 0.45, mix: 0.7 },
        multiband: { depth: 0.35 }
      })
    }
  },
  // ---- Bass
  {
    name: 'Core Reese Bass', category: 'Bass', tags: ['Core', 'Reese', 'DnB', 'Detuned'],
    core: {
      voice: voice({ voices: 4, velocityToAmp: 0.3, level: 0.25 }),
      oscA: osc({ position: 2 / 3, unison: 5, detune: 24, blend: 0.85, width: 0.35, level: 0.7 }),
      oscB: osc({ enabled: true, position: 2 / 3, unison: 3, detune: 11, blend: 0.8, width: 0.3, level: 0.55, fine: 7 }),
      sub: { level: 0.5, octave: -1, shape: 'sine', pan: 0 },
      filter: { enabled: true, mode: 'lp24', cutoff: 950, resonance: 0.2, drive: 0.3, keytrack: 0.25, envAmount: 0.6, velocityAmount: 0.3 },
      ampEnv: env({ attack: 0.005, decay: 0.5, sustain: 0.9, release: 0.18 }),
      modEnv: env({ attack: 0.002, decay: 0.4, sustain: 0.3, release: 0.2 }),
      lfos: lfos({ 0: { shape: 'sine', rate: 0.22, mode: 'free' } }),
      matrix: routes(['lfo1', 'oscA.detune', 0.35], ['lfo1', 'filter.cutoff', 0.08], ['modWheel', 'filter.cutoff', 0.35]),
      fx: rack({ distortion: { mode: 'tube', drive: 0.35, mix: 0.45, tone: 7000 }, multiband: { depth: 0.4 } })
    }
  },
  {
    name: 'Core Wobble Bass', category: 'Bass', tags: ['Core', 'Dubstep', 'Wobble', 'LFO'],
    core: {
      voice: voice({ mode: 'legato', voices: 1, glide: 0.04, glideMode: 'legato', velocityToAmp: 0.2, level: 0.3 }),
      oscA: osc({ position: 2 / 3, unison: 3, detune: 14, blend: 0.7, width: 0.4, level: 0.75 }),
      oscB: osc({ enabled: true, position: 1, octave: -1, unison: 1, level: 0.45 }),
      sub: { level: 0.4, octave: -1, shape: 'sine', pan: 0 },
      filter: { enabled: true, mode: 'lp24', cutoff: 260, resonance: 0.45, drive: 0.45, keytrack: 0.3, envAmount: 0, velocityAmount: 0 },
      ampEnv: env({ attack: 0.003, decay: 0.3, sustain: 1, release: 0.12 }),
      modEnv: env({ attack: 0.001, decay: 0.2, sustain: 0, release: 0.1 }),
      lfos: lfos({ 0: { shape: 'sine', sync: true, division: 0.5, mode: 'retrigger', phase: 0.75 } }),
      // Macro 1 deepens the wobble; the mod wheel speeds nothing, it opens the filter.
      matrix: routes(['lfo1', 'filter.cutoff', 0.5], ['lfo1', 'filter.cutoff', 0.2, 'macro1'], ['modWheel', 'filter.cutoff', 0.4]),
      fx: rack({ distortion: { mode: 'tube', drive: 0.5, mix: 0.6, tone: 9000 }, multiband: { depth: 0.5 } })
    }
  },
  {
    name: 'Core FM Growl', category: 'Bass', tags: ['Core', 'FM', 'Growl', 'Neuro'],
    core: {
      voice: voice({ mode: 'mono', voices: 1, glide: 0.03, glideMode: 'legato', velocityToAmp: 0.25, level: 0.26 }),
      oscA: osc({ table: 2, position: 0.35, unison: 1, randomPhase: 0, level: 0.85, warp: { mode: 'fm', amount: 0.35, envAmount: 0 } }),
      oscB: osc({ enabled: true, position: 0, octave: 0, unison: 1, randomPhase: 0, level: 0 }),
      sub: { level: 0.4, octave: -1, shape: 'sine', pan: 0 },
      filter: { enabled: true, mode: 'lp12', cutoff: 3200, resonance: 0.25, drive: 0.35, keytrack: 0.3, envAmount: 0, velocityAmount: 0 },
      ampEnv: env({ attack: 0.003, decay: 0.3, sustain: 1, release: 0.1 }),
      modEnv: env({ attack: 0.001, decay: 0.3, sustain: 0.2, release: 0.1 }),
      lfos: lfos({ 0: { shape: 'triangle', sync: true, division: 1, mode: 'retrigger' }, 1: { shape: 'saw-down', sync: true, division: 0.25, mode: 'retrigger' } }),
      matrix: routes(['lfo1', 'oscA.warp', 0.4], ['lfo1', 'oscA.position', 0.3], ['lfo2', 'filter.cutoff', 0.15], ['macro1', 'oscA.warp', 0.5]),
      fx: rack({ distortion: { mode: 'fold', drive: 0.3, mix: 0.4 }, multiband: { depth: 0.6 }, eq: { lowGain: 2, lowFrequency: 90 } })
    }
  },
  {
    name: 'Core Pluck Bass', category: 'Bass', tags: ['Core', 'Pluck', 'House'],
    core: {
      voice: voice({ voices: 4, velocityToAmp: 0.6, level: 1.5 }),
      oscA: osc({ position: 2 / 3, unison: 1, randomPhase: 0, level: 0.8 }),
      sub: { level: 0.45, octave: -1, shape: 'sine', pan: 0 },
      filter: { enabled: true, mode: 'lp24', cutoff: 380, resonance: 0.3, drive: 0.2, keytrack: 0.4, envAmount: 3.6, velocityAmount: 1 },
      ampEnv: env({ attack: 0.001, decay: 0.35, sustain: 0.15, release: 0.12 }),
      modEnv: env({ attack: 0.001, decay: 0.18, sustain: 0, release: 0.1 }),
      fx: rack({ compressor: { threshold: -20, ratio: 4, attack: 5, release: 80 } })
    }
  },
  // ---- Leads
  {
    name: 'Core Hyper Lead', category: 'Lead', tags: ['Core', 'Trance', 'Wide', 'Vibrato'],
    core: {
      voice: voice({ voices: 8, velocityToAmp: 0.4, level: 1.5 }),
      oscA: osc({ position: 2 / 3, unison: 7, detune: 30, blend: 0.8, width: 1, level: 0.75 }),
      oscB: osc({ enabled: true, position: 1, semi: 7, unison: 3, detune: 14, level: 0.22 }),
      filter: { enabled: true, mode: 'lp24', cutoff: 5500, resonance: 0.15, drive: 0.1, keytrack: 0.3, envAmount: 1, velocityAmount: 0.3 },
      ampEnv: env({ attack: 0.004, decay: 0.3, sustain: 0.85, release: 0.3 }),
      modEnv: env({ attack: 0.001, decay: 0.35, sustain: 0.2, release: 0.3 }),
      // Delayed vibrato, deeper on the mod wheel.
      lfos: lfos({ 0: { shape: 'sine', rate: 5.5, mode: 'retrigger', delay: 0.25, rise: 0.4 } }),
      matrix: routes(['lfo1', 'pitch', 0.006], ['lfo1', 'pitch', 0.02, 'modWheel']),
      fx: rack({
        hyper: { mix: 0.4, detune: 0.3, dimension: 0.3 },
        delay: { sync: true, division: 0.75, mode: 'ping-pong', feedback: 0.35, mix: 0.18, highCut: 7000 },
        reverb: { size: 1.4, decay: 2.6, mix: 0.18 }
      })
    }
  },
  {
    name: 'Core Glide Lead', category: 'Lead', tags: ['Core', 'Mono', 'Portamento', 'G-Funk'],
    core: {
      voice: voice({ mode: 'legato', voices: 1, glide: 0.09, glideMode: 'legato', velocityToAmp: 0.4, modWheelToCutoff: 1.5, level: 0.75 }),
      oscA: osc({ position: 0.4, unison: 1, randomPhase: 0, level: 0.75 }),
      oscB: osc({ enabled: true, position: 2 / 3, octave: 1, unison: 1, randomPhase: 0, level: 0.2 }),
      sub: { level: 0.25, octave: -1, shape: 'sine', pan: 0 },
      filter: { enabled: true, mode: 'lp12', cutoff: 3000, resonance: 0.2, drive: 0.1, keytrack: 0.5, envAmount: 0.8, velocityAmount: 0.4 },
      ampEnv: env({ attack: 0.01, decay: 0.3, sustain: 0.9, release: 0.25 }),
      modEnv: env({ attack: 0.001, decay: 0.3, sustain: 0.3, release: 0.2 }),
      lfos: lfos({ 0: { shape: 'sine', rate: 5, mode: 'retrigger', delay: 0.35, rise: 0.3 } }),
      matrix: routes(['lfo1', 'pitch', 0.012], ['aftertouch', 'filter.cutoff', 0.25]),
      fx: rack({ distortion: { mode: 'soft', drive: 0.2, mix: 0.4 }, delay: { sync: true, division: 0.5, feedback: 0.3, mix: 0.14 }, reverb: { mix: 0.15 } })
    }
  },
  {
    name: 'Core Acid Arp', category: 'Lead', tags: ['Core', 'Acid', '303', 'Arp'],
    arp: { enabled: true, mode: 'up', rate: 0.25, octaves: 2, gate: 0.55, swing: 0.12, ...steps('x^xx-x_x^.xx^x-x', [1, 1, 0.7, 1, 1, 0.8, 1, 1, 1, 1, 0.7, 1, 1, 0.8, 1, 1]) },
    core: {
      voice: voice({ mode: 'legato', voices: 1, glide: 0.05, glideMode: 'legato', velocityToAmp: 0.3, level: 0.71 }),
      oscA: osc({ position: 2 / 3, unison: 1, randomPhase: 0, level: 0.8 }),
      filter: { enabled: true, mode: 'ladder24', cutoff: 360, resonance: 0.78, drive: 0.45, keytrack: 0.3, envAmount: 4.4, velocityAmount: 1.6 },
      ampEnv: env({ attack: 0.002, decay: 0.3, sustain: 0.75, release: 0.08 }),
      modEnv: env({ attack: 0.001, decay: 0.2, sustain: 0.05, release: 0.1 }),
      matrix: routes(['macro1', 'filter.cutoff', 0.4], ['macro2', 'filter.resonance', 0.25]),
      fx: rack({ distortion: { mode: 'tube', drive: 0.35, mix: 0.6 }, delay: { sync: true, division: 0.75, feedback: 0.35, mix: 0.16, style: 'tape' } })
    }
  },
  // ---- Pads
  {
    name: 'Core Glass Pad', category: 'Pad', tags: ['Core', 'Evolving', 'Airy', 'Wavetable'],
    core: {
      voice: voice({ voices: 16, velocityToAmp: 0.3, level: 1.5 }),
      oscA: osc({ table: 2, position: 0.2, unison: 4, detune: 12, width: 0.9, level: 1 }),
      oscB: osc({ enabled: true, table: 4, position: 0.5, octave: 1, unison: 3, detune: 8, level: 0.45 }),
      filter: { enabled: true, mode: 'lp12', cutoff: 7000, resonance: 0.1, drive: 0, keytrack: 0.3, envAmount: 0.5, velocityAmount: 0 },
      ampEnv: env({ attack: 1.2, decay: 1.5, sustain: 0.85, release: 3, attackCurve: -0.2 }),
      modEnv: env({ attack: 2.5, decay: 2, sustain: 0.6, release: 3 }),
      lfos: lfos({ 0: { shape: 'sine', rate: 0.09, mode: 'free' }, 1: { shape: 'smooth-random', rate: 0.23, mode: 'free' } }),
      matrix: routes(['lfo1', 'oscA.position', 0.3], ['lfo2', 'oscB.position', 0.35], ['lfo2', 'oscB.pan', 0.4]),
      fx: rack({ chorus: { mix: 0.35 }, eq: { highGain: 2.5, highFrequency: 9000 }, reverb: { size: 2.2, decay: 6, preDelay: 40, mix: 0.42 } })
    }
  },
  {
    name: 'Core Vowel Choir', category: 'Pad', tags: ['Core', 'Choir', 'Formant', 'Vocal'],
    core: {
      voice: voice({ voices: 12, velocityToAmp: 0.3, level: 1.5 }),
      oscA: osc({ table: 4, position: 0.3, unison: 5, detune: 14, width: 0.9, level: 1 }),
      oscB: osc({ enabled: true, position: 2 / 3, unison: 3, detune: 10, level: 0.7 }),
      filter: { enabled: true, mode: 'formant', cutoff: 800, resonance: 0.25, drive: 0, keytrack: 0, envAmount: 0, velocityAmount: 0 },
      ampEnv: env({ attack: 0.6, decay: 1, sustain: 0.9, release: 2 }),
      modEnv: env({ attack: 1.5, decay: 2, sustain: 0.5, release: 2 }),
      lfos: lfos({ 0: { shape: 'smooth-random', rate: 0.35, mode: 'free' }, 1: { shape: 'sine', rate: 5.2, mode: 'retrigger', delay: 0.4, rise: 0.6 } }),
      matrix: routes(['lfo1', 'filter.cutoff', 0.15], ['lfo1', 'oscA.position', 0.25], ['lfo2', 'pitch', 0.004], ['modWheel', 'filter.cutoff', 0.25]),
      fx: rack({ hyper: { mix: 0.25, dimension: 0.5, size: 0.5 }, reverb: { size: 2, decay: 4.5, preDelay: 30, mix: 0.3 } })
    }
  },
  {
    name: 'Core Dark Drone', category: 'Pad', tags: ['Core', 'Cinematic', 'Drone', 'Dark'],
    core: {
      voice: voice({ voices: 8, velocityToAmp: 0.2, level: 1.5 }),
      oscA: osc({ table: 3, position: 0.1, unison: 3, detune: 9, width: 0.8, level: 0.7, warp: { mode: 'bend+', amount: 0.2, envAmount: 0 } }),
      sub: { level: 0.35, octave: -1, shape: 'triangle', pan: 0 },
      noise: { level: 0.06, color: 0.8, pan: 0 },
      filter: { enabled: true, mode: 'lp24', cutoff: 900, resonance: 0.3, drive: 0.2, keytrack: 0.3, envAmount: 0.6, velocityAmount: 0 },
      ampEnv: env({ attack: 2, decay: 2, sustain: 0.9, release: 4, attackCurve: -0.3 }),
      modEnv: env({ attack: 4, decay: 3, sustain: 0.5, release: 4 }),
      lfos: lfos({ 0: { shape: 'sine', rate: 0.05, mode: 'free' }, 1: { shape: 'triangle', rate: 0.13, mode: 'free' } }),
      matrix: routes(['lfo1', 'filter.cutoff', 0.15], ['lfo2', 'oscA.warp', 0.3], ['lfo2', 'oscA.position', 0.2]),
      fx: rack({ phaser: { rate: 0.12, depth: 0.7, mix: 0.4, feedback: 0.5 }, reverb: { size: 2.6, decay: 9, preDelay: 60, mix: 0.5, damping: 4000 } })
    }
  },
  {
    name: 'Core Pump Pad', category: 'Pad', tags: ['Core', 'Sidechain', 'EDM', 'Supersaw'],
    core: {
      voice: voice({ voices: 16, velocityToAmp: 0.2, level: 0.86 }),
      oscA: osc({ position: 2 / 3, unison: 9, detune: 32, blend: 0.8, width: 1, level: 0.7 }),
      oscB: osc({ enabled: true, position: 2 / 3, octave: 1, unison: 5, detune: 20, level: 0.25 }),
      filter: { enabled: true, mode: 'lp24', cutoff: 4200, resonance: 0.1, drive: 0, keytrack: 0.2, envAmount: 0, velocityAmount: 0 },
      ampEnv: env({ attack: 0.05, decay: 1, sustain: 1, release: 0.8 }),
      // A saw-down LFO on the beat ducks the pad like a sidechain.
      lfos: lfos({ 0: { shape: 'saw-up', sync: true, division: 1, mode: 'free' } }),
      matrix: routes(['lfo1', 'amp', -0.45]),
      fx: rack({ hyper: { mix: 0.3, dimension: 0.3 }, reverb: { size: 1.6, decay: 3, mix: 0.25 } })
    }
  },
  // ---- Keys and plucks
  {
    name: 'Core FM Bell Keys', category: 'Keys', tags: ['Core', 'FM', 'Bell', 'EP'],
    core: {
      voice: voice({ voices: 16, velocityToAmp: 0.7, level: 0.9 }),
      oscA: osc({ position: 0, unison: 1, randomPhase: 0, level: 0.8, warp: { mode: 'fm', amount: 0.1, envAmount: 0.35 } }),
      oscB: osc({ enabled: true, position: 0, octave: 2, semi: 0, unison: 1, randomPhase: 0, level: 0.05 }),
      filter: { enabled: false, mode: 'lp24', cutoff: 12000, resonance: 0.1, drive: 0, keytrack: 0, envAmount: 0, velocityAmount: 0 },
      ampEnv: env({ attack: 0.001, decay: 2.4, sustain: 0, release: 1.2, decayCurve: 0.6 }),
      modEnv: env({ attack: 0.001, decay: 0.7, sustain: 0.05, release: 0.6 }),
      matrix: routes(['velocity', 'oscA.warp', 0.25]),
      fx: rack({ chorus: { mix: 0.22, depth: 0.3 }, reverb: { size: 1.3, decay: 2.2, mix: 0.24 } })
    }
  },
  {
    name: 'Core Tine Keys', category: 'Keys', tags: ['Core', 'Electric piano', 'Tremolo'],
    core: {
      voice: voice({ voices: 16, velocityToAmp: 0.8, level: 0.57 }),
      oscA: osc({ position: 0, unison: 1, randomPhase: 0, level: 0.8 }),
      oscB: osc({ enabled: true, position: 1 / 3, octave: 1, unison: 1, randomPhase: 0, level: 0.15 }),
      filter: { enabled: true, mode: 'lp12', cutoff: 5000, resonance: 0.05, drive: 0.15, keytrack: 0.5, envAmount: 1.5, velocityAmount: 1.2 },
      ampEnv: env({ attack: 0.002, decay: 3, sustain: 0.1, release: 0.5, decayCurve: 0.5 }),
      modEnv: env({ attack: 0.001, decay: 0.4, sustain: 0, release: 0.3 }),
      lfos: lfos({ 0: { shape: 'sine', rate: 4.5, mode: 'free' } }),
      matrix: routes(['velocity', 'oscB.level', 0.4], ['lfo1', 'amp', -0.12], ['lfo1', 'oscA.pan', 0.3], ['modWheel', 'amp', -0.25]),
      fx: rack({ phaser: { rate: 0.3, mix: 0.25, depth: 0.5 }, reverb: { size: 1.1, decay: 1.8, mix: 0.2 } })
    }
  },
  {
    name: 'Core Future Pluck', category: 'Pluck', tags: ['Core', 'Future bass', 'Pluck', 'Wide'],
    core: {
      voice: voice({ voices: 16, velocityToAmp: 0.5, level: 1.5 }),
      oscA: osc({ position: 2 / 3, unison: 5, detune: 22, blend: 0.8, width: 1, level: 0.75 }),
      oscB: osc({ enabled: true, position: 1, octave: 1, unison: 3, detune: 12, level: 0.2 }),
      filter: { enabled: true, mode: 'lp24', cutoff: 800, resonance: 0.25, drive: 0.1, keytrack: 0.4, envAmount: 4, velocityAmount: 1 },
      ampEnv: env({ attack: 0.001, decay: 0.45, sustain: 0, release: 0.3 }),
      modEnv: env({ attack: 0.001, decay: 0.24, sustain: 0, release: 0.2 }),
      fx: rack({
        hyper: { mix: 0.3 }, multiband: { depth: 0.3 },
        delay: { sync: true, division: 0.5, mode: 'ping-pong', feedback: 0.3, mix: 0.2 },
        reverb: { size: 1.4, decay: 2.2, mix: 0.2 }
      })
    }
  },
  {
    name: 'Core Arp Pluck', category: 'Pluck', tags: ['Core', 'Arp', 'Trance', 'Pluck'],
    arp: { enabled: true, mode: 'up-down', rate: 0.25, octaves: 2, gate: 0.45, swing: 0.1, ...steps('xxxxxxxxxxxxxxxx', [1, 0.6, 0.8, 0.6, 1, 0.6, 0.8, 0.6, 1, 0.6, 0.8, 0.6, 1, 0.6, 0.8, 0.6]) },
    core: {
      voice: voice({ voices: 16, velocityToAmp: 0.6, level: 1.5 }),
      oscA: osc({ position: 2 / 3, unison: 3, detune: 14, width: 0.8, level: 0.75 }),
      oscB: osc({ enabled: true, position: 1, octave: 1, unison: 1, level: 0.15 }),
      filter: { enabled: true, mode: 'lp24', cutoff: 1100, resonance: 0.3, drive: 0.1, keytrack: 0.5, envAmount: 3, velocityAmount: 1.2 },
      ampEnv: env({ attack: 0.001, decay: 0.3, sustain: 0, release: 0.2 }),
      modEnv: env({ attack: 0.001, decay: 0.16, sustain: 0, release: 0.1 }),
      matrix: routes(['macro1', 'filter.cutoff', 0.35]),
      fx: rack({ delay: { sync: true, division: 0.75, mode: 'ping-pong', feedback: 0.4, mix: 0.2 }, reverb: { size: 1.5, decay: 2.8, mix: 0.24 } })
    }
  },
  // ---- FX
  {
    name: 'Core Macro Riser', category: 'FX', tags: ['Core', 'Riser', 'Noise', 'Macro'],
    core: {
      voice: voice({ voices: 4, velocityToAmp: 0.2, level: 1.5 }),
      oscA: osc({ position: 2 / 3, unison: 7, detune: 40, width: 1, level: 0.9 }),
      noise: { level: 1, color: 0.4, pan: 0 },
      filter: { enabled: true, mode: 'bp12', cutoff: 500, resonance: 0.35, drive: 0, keytrack: 0, envAmount: 2.5, velocityAmount: 0 },
      ampEnv: env({ attack: 0.3, decay: 1, sustain: 1, release: 1.5 }),
      modEnv: env({ attack: 4, decay: 1, sustain: 1, release: 1.5, attackCurve: -0.4 }),
      lfos: lfos({ 0: { shape: 'saw-up', sync: true, division: 0.25, mode: 'retrigger' } }),
      // Macro 1 (or the mod wheel) is the build: filter up, pitch up, more space.
      matrix: routes(['macro1', 'filter.cutoff', 0.6], ['macro1', 'pitch', 0.25], ['macro1', 'fx.reverb.mix', 0.4],
        ['modWheel', 'filter.cutoff', 0.6], ['lfo1', 'amp', -0.3, 'macro1']),
      fx: rack({ flanger: { mix: 0.4, rate: 0.15, feedback: 0.6 }, reverb: { size: 2.4, decay: 5, mix: 0.3 } })
    }
  }
];

export const CORE_PRESETS = CORE_PATCHES.map(({ name, category, tags, core, arp = null }) => {
  const base = createDefaultInstrumentPatch();
  return normalizeInstrumentPatch({
    ...base,
    id: `core-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    name,
    category,
    tags,
    macros: Object.fromEntries(Object.keys(base.macros).map((key) => [key, 0])),
    layers: base.layers.map((layer, index) => ({
      ...layer,
      enabled: index === 0,
      engine: index === 0 ? 'core' : layer.engine,
      name: index === 0 ? 'CookUp Core' : layer.name,
      level: index === 0 ? 0.9 : layer.level,
      pan: 0,
      // Tuning lives in the Core oscillators; the layer itself stays neutral.
      ...(index === 0 ? { octave: 0, semitone: 0, detune: 0 } : {}),
      ...(index === 0 ? { core: { ...defaultCorePatch(), ...core } } : {})
    })),
    // The Core layer has its own filter, envelopes and voice handling.
    filter: { ...base.filter, enabled: false },
    filterEnvelope: { ...base.filterEnvelope, amount: 0 },
    ampEnvelope: { attack: 0.001, decay: 0.1, sustain: 1, release: 0.05, velocity: 0 },
    lfo: { ...base.lfo, enabled: false },
    lfo2: { ...base.lfo2, enabled: false },
    effects: {
      ...base.effects,
      distortion: { ...base.effects.distortion, enabled: false },
      chorus: { ...base.effects.chorus, enabled: false },
      delay: { ...base.effects.delay, enabled: false },
      reverb: { ...base.effects.reverb, enabled: false },
      compressor: { ...base.effects.compressor, enabled: false }
    },
    performance: { ...base.performance, master: 0.72, glide: 0, voices: 32 },
    ...(arp ? { arp: { ...base.arp, ...arp } } : {})
  });
});
